import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDailyTrades, DailyTrade } from "../api/client";

/**
 * Daily trade blotter: every fill the bot made on a given day — what was
 * bought/sold, the $ value, realized P&L (on closes), and the running /
 * ending account equity. Date-navigable; polls every 30s when viewing today
 * so it stays near-real-time during the session.
 *
 * Colour is protan-safe: P&L is blue (+) / orange (−) and ALWAYS signed;
 * side is a plain BUY/SELL chip, never colour-alone.
 */
const BLUE = "#3b82f6";
const ORANGE = "#f59e0b";

const todayUTC = () => new Date().toISOString().slice(0, 10);
const shiftDay = (d: string, days: number) => {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};
const money = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const signedMoney = (v: number | null | undefined) => {
  if (v === null || v === undefined) return "—";
  const s = v >= 0 ? "+" : "−";
  return `${s}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const hhmmss = (iso: string) => {
  try { return new Date(iso).toLocaleTimeString(); } catch { return iso; }
};

export function DailyDecisionsPage() {
  const [date, setDate] = useState(todayUTC());
  const [mode, setMode] = useState<"paper" | "live">("paper");
  const isToday = date === todayUTC();

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["daily-trades", date, mode],
    queryFn: () => fetchDailyTrades(date, mode),
    refetchInterval: isToday ? 30_000 : false, // near-real-time only for today
    staleTime: 15_000,
  });

  const s = data?.summary;
  const pnlColor = (v: number | null | undefined) =>
    v === null || v === undefined ? "#cbd5e1" : v >= 0 ? BLUE : ORANGE;

  const rows = useMemo(() => data?.trades ?? [], [data]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ marginTop: 0 }}>Daily decisions</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {(["paper", "live"] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: mode === m ? "#1e3a8a" : "#0f172a", color: mode === m ? "#bfdbfe" : "#cbd5e1",
              border: "1px solid #334155", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
            }}>{m}</button>
          ))}
        </div>
      </div>

      <div className="card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setDate((d) => shiftDay(d, -1))} style={navBtn}>← Prev</button>
        <input type="date" value={date} max={todayUTC()} onChange={(e) => setDate(e.target.value)}
          style={{ background: "#0f172a", color: "#e2e8f0", border: "1px solid #334155", borderRadius: 4, padding: "6px 10px", fontSize: 13 }} />
        <button onClick={() => setDate((d) => shiftDay(d, 1))} disabled={isToday} style={navBtn}>Next →</button>
        <button onClick={() => setDate(todayUTC())} disabled={isToday} style={navBtn}>Today</button>
        {isToday && <span style={{ color: BLUE, fontSize: 12 }}>● live{isFetching ? " (refreshing…)" : " (auto-refresh 30s)"}</span>}
        {!isToday && <button onClick={() => refetch()} style={navBtn}>Refresh</button>}
      </div>

      {error && <div className="card" style={{ color: ORANGE }}>Failed to load: {String((error as Error).message || error)}</div>}

      {s && (
        <div className="card" style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
          <div><span style={{ color: "#94a3b8" }}>Trades</span> <strong>{s.trade_count}</strong>
            <span style={{ color: "#64748b" }}> ({s.buys} buy / {s.sells} sell)</span></div>
          <div><span style={{ color: "#94a3b8" }}>Traded value</span> <strong>{money(s.notional_total)}</strong></div>
          <div><span style={{ color: "#94a3b8" }}>Realized P&amp;L</span>{" "}
            <strong style={{ color: pnlColor(s.realized_pl_total) }}>{signedMoney(s.realized_pl_total)}</strong></div>
          <div><span style={{ color: "#94a3b8" }}>Day P&amp;L (equity)</span>{" "}
            <strong style={{ color: pnlColor(s.day_pl) }}>{signedMoney(s.day_pl)}</strong></div>
          <div><span style={{ color: "#94a3b8" }}>Start → End equity</span>{" "}
            <strong>{money(s.start_equity)} → {money(s.end_equity)}</strong></div>
        </div>
      )}

      <div className="card" style={{ overflowX: "auto" }}>
        {isLoading ? (
          <div style={{ color: "#94a3b8" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>
            No trades recorded for {date} ({mode}). The blotter captures fills from when the feature
            was deployed forward — earlier days won't have rows unless backfilled.
          </div>
        ) : (
          <table style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Time</th>
                <th style={{ textAlign: "left" }}>Ticker</th>
                <th style={{ textAlign: "left" }}>Side</th>
                <th className="num">Qty</th>
                <th className="num">Fill price</th>
                <th className="num">Value</th>
                <th className="num" title="Realized P&L on closing trades (sell − cost basis). Buys show — (no realized P&L until closed).">Realized P&amp;L</th>
                <th className="num">Equity after</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t: DailyTrade, i: number) => (
                <tr key={`${t.executed_at}-${t.ticker}-${i}`}>
                  <td style={{ color: "#94a3b8", whiteSpace: "nowrap" }}>{hhmmss(t.executed_at)}</td>
                  <td style={{ fontWeight: 600 }}>{t.ticker}</td>
                  <td>
                    <span style={{
                      border: `1px solid ${t.side === "buy" ? BLUE : ORANGE}`,
                      color: t.side === "buy" ? BLUE : ORANGE,
                      borderRadius: 4, padding: "1px 7px", fontSize: 11.5, textTransform: "uppercase",
                    }}>{t.side}</span>
                  </td>
                  <td className="num" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {t.qty === null ? "—" : Math.round(t.qty)}
                  </td>
                  <td className="num" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {t.fill_price === null ? "—" : `$${t.fill_price.toFixed(2)}`}
                  </td>
                  <td className="num" style={{ fontVariantNumeric: "tabular-nums" }}>{money(t.notional)}</td>
                  <td className="num" style={{ fontVariantNumeric: "tabular-nums", color: pnlColor(t.realized_pl) }}>
                    {t.realized_pl === null ? "—" : signedMoney(t.realized_pl)}
                  </td>
                  <td className="num" style={{ fontVariantNumeric: "tabular-nums", color: "#cbd5e1" }}>
                    {money(t.account_equity_after)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const navBtn: React.CSSProperties = {
  background: "#0f172a", color: "#cbd5e1", border: "1px solid #334155",
  borderRadius: 4, padding: "6px 12px", fontSize: 12, cursor: "pointer",
};
